/**
 * Account self-service — API keys and two-factor — against the folded-in
 * portal endpoints (`/pwa/api/account/*`). Same session cookie as the rest of
 * the app; no separate portal login.
 */
import { api } from './api';

export interface Account {
  email: string;
  plan: string;
  mfa_enabled: boolean;
}

export interface ApiKey {
  id: string;
  prefix: string;
  name: string;
  active: boolean;
  created_at: string;
  last_used_at: string | null;
}

export const getAccount = () => api.get<Account>('/pwa/api/account');

export const listKeys = () => api.get<{ keys: ApiKey[] }>('/pwa/api/account/keys');

/** Returns the full key ONCE — it is never retrievable again. */
export const createKey = (name: string) =>
  api.post<{ key: ApiKey; plaintext: string }>('/pwa/api/account/keys', { name });

export const revokeKey = (id: string) =>
  api.del<void>(`/pwa/api/account/keys/${encodeURIComponent(id)}`);

// --- two-factor -----------------------------------------------------

export interface TotpSetup {
  secret: string;
  otpauth_uri: string;
  qr_svg: string;
}

export const totpSetup = () => api.post<TotpSetup>('/pwa/api/account/mfa/totp/setup');

export const totpConfirm = (code: string) =>
  api.post<{ mfa_enabled: boolean }>('/pwa/api/account/mfa/totp/confirm', { code });

export const totpDisable = (password: string) =>
  api.del<{ mfa_enabled: boolean }>('/pwa/api/account/mfa/totp', { password });

export const logoutAll = () => api.post<{ ok: boolean }>('/pwa/auth/logout-all');

// --- linked OAuth identities --------------------------------------------

export interface OAuthIdentity {
  provider: 'google';
  email: string;
  linked_at: string;
}

export const listIdentities = () =>
  api.get<{ identities: OAuthIdentity[] }>('/pwa/auth/oauth/identities');

/** Full-page nav — the backend redirects to Google and back to Settings. */
export const GOOGLE_LINK_PATH = '/pwa/auth/oauth/google/link';

export const unlinkGoogle = () => api.post<{ ok: boolean }>('/pwa/auth/oauth/google/unlink');

/** Change the account password. `totpCode` is required when two-factor is on.
 *  Other devices are signed out; this one stays signed in. */
export const changePassword = (current: string, next: string, totpCode?: string) =>
  api.post<{ ok: boolean }>('/pwa/api/account/password', {
    current_password: current,
    new_password: next,
    ...(totpCode ? { totp_code: totpCode } : {}),
  });
